import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { auth } from '../config/firebase';
import { AuthService } from '../services/authService';
import { mentionSearchService } from '../services/mentionSearchService';
import {
  AuthState,
  AuthContextType,
  User,
  UserRole,
  RegisterFormData,
  UserProfile
} from '../types/auth';

// Create the authentication context
const AuthContext = createContext<AuthContextType | null>(null);

// Initial auth state
const initialAuthState: AuthState = {
  user: null,
  loading: true,
  error: null,
  isAuthenticated: false,
  isEmailVerified: false,
};

// AuthProvider component props
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Authentication Provider Component
 * Manages authentication state and provides auth methods to the app
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>(initialAuthState);

  /**
   * Update authentication state
   */
  const updateAuthState = (updates: Partial<AuthState>) => {
    setAuthState(prev => ({ ...prev, ...updates }));
  };

  /**
   * Set loading state
   */
  const setLoading = useCallback((loading: boolean) => {
    updateAuthState({ loading });
  }, []);

  /**
   * Set error state
   */
  const setError = useCallback((error: string | null) => {
    updateAuthState({ error });
  }, []);

  /**
   * Set user data
   */
  const setUser = useCallback((user: User | null) => {
    updateAuthState({
      user,
      isAuthenticated: !!user,
      isEmailVerified: user?.isEmailVerified || false,
      error: null,
    });
  }, []);

  /**
   * Register a new user
   */
  const register = async (formData: RegisterFormData): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      
      const userData = await AuthService.register(formData);
      setUser(userData);
    } catch (error: any) {
      setError(error.message || 'Registration failed');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sign in user
   */
  const login = async (email: string, password: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      
      const userData = await AuthService.signIn(email, password);
      setUser(userData);
    } catch (error: any) {
      setError(error.message || 'Login failed');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sign out user
   */
  const logout = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      
      await AuthService.signOut();
      setUser(null);
    } catch (error: any) {
      setError(error.message || 'Logout failed');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Send password reset email
   */
  const resetPassword = async (email: string): Promise<void> => {
    try {
      setError(null);
      await AuthService.sendPasswordReset(email);
    } catch (error: any) {
      setError(error.message || 'Password reset failed');
      throw error;
    }
  };

  /**
   * Send email verification
   */
  const verifyEmail = async (): Promise<void> => {
    try {
      setError(null);
      await AuthService.sendEmailVerification();
    } catch (error: any) {
      setError(error.message || 'Email verification failed');
      throw error;
    }
  };

  /**
   * Update user profile
   */
  const updateProfile = async (data: Partial<UserProfile>): Promise<void> => {
    try {
      if (!authState.user) {
        throw new Error('No authenticated user');
      }

      setLoading(true);
      setError(null);
      
      await AuthService.updateUserProfile(authState.user.uid, data);
      
      // Refresh user data
      const updatedUser = await AuthService.getCurrentUser();
      if (updatedUser) {
        setUser(updatedUser);
      }
    } catch (error: any) {
      setError(error.message || 'Profile update failed');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Refresh current user data
   */
  const refreshUser = async (): Promise<void> => {
    try {
      const currentUser = await AuthService.getCurrentUser();
      setUser(currentUser);
    } catch (error: any) {
      console.error('Error refreshing user:', error);
      setError('Failed to refresh user data');
    }
  };

  /**
   * Listen for authentication state changes
   */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      try {
        if (firebaseUser) {
          // User is signed in, get their data from Firestore
          const userData = await AuthService.getCurrentUser();
          setUser(userData);

          // Index user for mention search (non-blocking)
          if (userData) {
            mentionSearchService.indexUser(
              userData.uid,
              userData.displayName || userData.email,
              userData.email,
              userData.role === 'admin' || userData.role === 'editor' || userData.role === 'contributor',
              userData.personId,
              undefined, // personName - would need to fetch from person document
              userData.photoURL
            ).catch(err => console.error('Failed to index user for mentions:', err));
          }
        } else {
          // User is signed out
          setUser(null);
        }
      } catch (error: any) {
        console.error('Auth state change error:', error);
        setError('Authentication error occurred');
        setUser(null);
      } finally {
        setLoading(false);
      }
    });

    // Cleanup subscription
    return () => unsubscribe();
  }, [setError, setLoading, setUser]);

  /**
   * Context value
   */
  const contextValue: AuthContextType = {
    authState,
    login,
    register,
    logout,
    resetPassword,
    verifyEmail,
    updateProfile,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom hook to use authentication context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

/**
 * Hook to get current user
 */
export const useUser = (): User | null => {
  const { authState } = useAuth();
  return authState.user;
};

/**
 * Hook to check if user has specific role
 */
export const useHasRole = (requiredRole: UserRole): boolean => {
  const user = useUser();
  return AuthService.hasRole(user, requiredRole);
};

/**
 * Hook to check if user can perform specific action
 */
export const useCanPerform = (action: string): boolean => {
  const user = useUser();
  return AuthService.canPerformAction(user, action);
};

/**
 * Hook to get authentication loading state
 */
export const useAuthLoading = (): boolean => {
  const { authState } = useAuth();
  return authState.loading;
};

/**
 * Hook to get authentication error
 */
export const useAuthError = (): string | null => {
  const { authState } = useAuth();
  return authState.error;
};

export default AuthContext;